import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, FileText, Pencil, Search, Users, X, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PdfPreviewDialog } from "@/components/PdfPreviewDialog";
import { toast } from "sonner";

const TH = "px-3 py-2 text-left text-[10px] uppercase tracking-[0.1em] text-[#6B7280]";

export default function PatientDirectory() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [pdf, setPdf] = useState(false);
  const [rename, setRename] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/patients/directory", { params: { search: query } });
      setRows(data.patients || []);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  const openPatient = async (ulid, knownName = "") => {
    setDetail({ ulid, patient_name: knownName, loading: true, visits: [] });
    try {
      const { data } = await api.get(`/patients/${encodeURIComponent(ulid)}/history`);
      setDetail({ ...data, loading: false });
    } catch (e) { toast.error(errMsg(e)); setDetail(null); }
  };

  const exportExcel = async () => {
    try {
      const res = await api.get("/patients/directory/export",
        { params: { search: query }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "labtrack_patients.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      toast.success("Directory exported");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveName = async () => {
    const name = (rename?.patient_name || "").trim();
    if (!name) return toast.error("Enter the correct patient name");
    try {
      await api.put(`/patients/${encodeURIComponent(rename.ulid)}`, { patient_name: name });
      toast.success("Registered name updated");
      setRename(null);
      load();
      if (detail?.ulid === rename.ulid) openPatient(rename.ulid);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="patient-directory-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">
            Patient Directory
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">
            Every ULID registered in this lab, with how often that patient has been seen.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportExcel} data-testid="directory-excel-btn">
            <Download className="h-4 w-4 mr-2 text-[#2E7D32]" /> Excel
          </Button>
          <Button variant="outline" onClick={() => setPdf(true)} data-testid="directory-pdf-btn">
            <FileText className="h-4 w-4 mr-2 text-[#C92A2A]" /> Master PDF
          </Button>
        </div>
      </div>

      <form className="mt-4 flex gap-2" data-testid="directory-search-form"
        onSubmit={(e) => { e.preventDefault(); setQuery(search); }}>
        <div className="flex items-center gap-2 flex-1 rounded-md border border-[#E5E7EB] bg-white px-3">
          <Search className="h-4 w-4 text-[#6B7280]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="directory-search-input" placeholder="Search by ULID or patient name…"
            className="flex-1 h-10 text-sm outline-none bg-transparent" />
        </div>
        <Button type="submit" data-testid="directory-search-btn"
          className="bg-[#002FA7] hover:bg-[#00237C] text-white">Search</Button>
      </form>

      <div className="mt-4 rounded-md border border-[#E5E7EB] bg-white overflow-hidden">
        <div className="px-3 py-2 border-b border-[#E5E7EB] flex items-center gap-2">
          <Users className="h-4 w-4 text-[#002FA7]" />
          <p className="text-xs text-[#6B7280]" data-testid="directory-count">
            {loading ? "Loading…" : `${rows.length} patient(s)`}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB]">
              <tr>
                {["ULID", "Patient", "Age", "Sex", "Ward", "ABHA", "First seen", "Last seen",
                  "Samples", "Custody entries", ""].map((h) => (
                  <th key={h} className={TH}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-8 text-center text-sm text-[#6B7280]"
                  data-testid="directory-empty">
                  {query
                    ? `No patient matches “${query}”. Try a different ULID or name.`
                    : "No patients registered yet. They appear here as soon as a ULID is used."}
                </td></tr>
              )}
              {rows.map((p) => (
                <tr key={p.ulid} className="border-t border-[#E5E7EB] hover:bg-gray-50"
                  data-testid={`patient-row-${p.ulid}`}>
                  <td className="px-3 py-2">
                    <button onClick={() => openPatient(p.ulid, p.patient_name)} data-testid={`patient-open-${p.ulid}`}
                      className="font-mono text-[#002FA7] hover:underline">{p.ulid}</button>
                  </td>
                  <td className="px-3 py-2 text-[#111827]">{p.patient_name || "—"}</td>
                  <td className="px-3 py-2">{p.age || "—"}</td>
                  <td className="px-3 py-2">{p.sex || "—"}</td>
                  <td className="px-3 py-2">{p.ward || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {p.abha_number
                      ? <span className="inline-flex items-center gap-1 text-[#2E7D32] font-mono text-xs"
                          data-testid={`patient-abha-${p.ulid}`} title={p.abha_address || ""}>
                          <ShieldCheck className="h-3.5 w-3.5" /> {p.abha_number}</span>
                      : <button onClick={() => navigate("/abdm")} data-testid={`patient-abha-link-${p.ulid}`}
                          className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1">
                          <ShieldCheck className="h-3.5 w-3.5" /> Link ABHA</button>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">{p.first_seen || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{p.last_seen || "—"}</td>
                  <td className="px-3 py-2">{p.samples}</td>
                  <td className="px-3 py-2">{p.traces}</td>
                  <td className="px-3 py-2 text-right">
                    {isAdmin && (
                      <button onClick={() => setRename({ ulid: p.ulid, patient_name: p.patient_name })}
                        title="Correct the registered name" data-testid={`patient-rename-${p.ulid}`}
                        className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#002FA7]">
                        <Pencil className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <div className="mt-4 rounded-md border border-[#E5E7EB] bg-white" data-testid="patient-detail-panel">
          <div className="px-4 py-3 border-b border-[#E5E7EB] flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-sm text-[#111827]">
                {detail.patient_name || "Unnamed patient"}
              </p>
              <p className="text-xs text-[#6B7280] font-mono">{detail.ulid}</p>
            </div>
            <button onClick={() => setDetail(null)} data-testid="patient-detail-close"
              className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#6B7280]"><X className="h-4 w-4" /></button>
          </div>
          <div className="divide-y divide-[#E5E7EB]">
            {detail.loading && <p className="p-4 text-sm text-[#6B7280]">Loading visit history…</p>}
            {!detail.loading && (detail.visits || []).length === 0 && (
              <p className="p-4 text-sm text-[#6B7280]" data-testid="patient-detail-empty">
                No samples or custody entries recorded against this ULID yet.
              </p>
            )}
            {(detail.visits || []).map((v) => (
              <div key={v.record_id} className="px-4 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1"
                data-testid={`patient-visit-${v.record_id}`}>
                <span className="text-xs text-[#6B7280] w-24">{v.date || "—"}</span>
                <span className="text-[10px] uppercase tracking-[0.08em] px-2 py-0.5 rounded-full border
                  border-[#CBD5E1] text-[#374151]">
                  {v.module === "samples" ? "Sample" : "Custody"}
                </span>
                <span className="text-sm text-[#111827] font-mono">{v.sample_id || "—"}</span>
                <span className="text-sm text-[#374151]">{v.test_name || v.nature_of_sample || "—"}</span>
                {v.status && <span className="text-xs text-[#6B7280]">{v.status}</span>}
                <span className={`text-xs ${v.custody === "Signed handover" || v.custody === "Linked"
                  ? "text-[#2E7D32]" : "text-[#B45309]"}`}>{v.custody}</span>
                {v.pending && (
                  <span className="text-[10px] uppercase tracking-[0.08em] text-[#B45309]">
                    details pending
                  </span>
                )}
                <button onClick={() => navigate(`/m/${v.module}?focus=${v.record_id}`)}
                  data-testid={`patient-visit-open-${v.record_id}`}
                  className="ml-auto text-xs text-[#002FA7] hover:underline">Open record</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {rename && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          data-testid="patient-rename-overlay" onClick={() => setRename(null)}>
          <div className="bg-white rounded-lg p-4 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <p className="font-heading font-bold text-sm text-[#111827]">Correct registered name</p>
            <p className="text-xs text-[#6B7280] mt-0.5 font-mono">{rename.ulid}</p>
            <Input value={rename.patient_name || ""} className="mt-3"
              data-testid="patient-rename-input"
              onChange={(e) => setRename({ ...rename, patient_name: e.target.value })} />
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setRename(null)}
                data-testid="patient-rename-cancel">Cancel</Button>
              <Button size="sm" onClick={saveName} data-testid="patient-rename-save"
                className="bg-[#002FA7] hover:bg-[#00237C] text-white">Save name</Button>
            </div>
          </div>
        </div>
      )}

      <PdfPreviewDialog open={pdf} onClose={() => setPdf(false)}
        path={pdf ? `/patients/directory/pdf${query ? `?search=${encodeURIComponent(query)}` : ""}` : null}
        filename="labtrack_patients.pdf" title="Patient Directory" />
    </div>
  );
}
